import { RequiredLabel } from '@protoss/ui';

export interface StackOptionFieldOption {
  label: string;
  value: string;
  description?: string;
  badgeLabel?: string;
  badgeTone?: 'neutral' | 'success' | 'warning' | 'danger';
}

interface StackOptionFieldProps {
  label?: string;
  helperText?: string;
  required?: boolean;
  showRequiredText?: boolean;
  options: StackOptionFieldOption[];
  selectedValue: string | null;
  onSelectionChange: (value: string) => void;
  error?: string;
  suppressErrorMessage?: boolean;
  disabled?: boolean;
}

export function StackOptionField({
  label,
  helperText,
  required = false,
  showRequiredText = false,
  options,
  selectedValue,
  onSelectionChange,
  error,
  suppressErrorMessage = false,
  disabled = false,
}: StackOptionFieldProps) {
  const id = `stack-option-${label?.toLowerCase().replace(/[^a-z0-9]+/g, '-') ?? 'field'}`;
  const hasError = Boolean(error);

  return (
    <div className="stack-option-field">
      {label || helperText ? (
        <div className="stack-option-field__header">
          {label ? (
            <span id={`${id}-label`} className="stack-option-field__label">
              <RequiredLabel
                label={label}
                required={required}
                showRequiredText={showRequiredText || hasError}
                secondaryText="Single select"
              />
            </span>
          ) : null}
          {helperText ? <span className="stack-option-field__helper">{helperText}</span> : null}
        </div>
      ) : null}

      <div
        className="stack-option-field__list"
        role="radiogroup"
        aria-labelledby={label ? `${id}-label` : undefined}
      >
        {options.map((option) => {
          const selected = selectedValue === option.value;
          const tone = option.badgeTone ?? 'neutral';

          return (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={selected}
              disabled={disabled}
              className={[
                'stack-option-field__option',
                selected ? 'stack-option-field__option--selected' : '',
                disabled ? 'stack-option-field__option--disabled' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              onClick={() => onSelectionChange(option.value)}
            >
              <span className="stack-option-field__indicator" aria-hidden="true">
                <span className="stack-option-field__indicator-dot" />
              </span>
              <span className="stack-option-field__content">
                <span className="stack-option-field__title">{option.label}</span>
                {option.description ? (
                  <span className="stack-option-field__description">{option.description}</span>
                ) : null}
                {option.badgeLabel ? (
                  <span className={`stack-option-field__badge stack-option-field__badge--${tone}`}>
                    {option.badgeLabel}
                  </span>
                ) : null}
              </span>
            </button>
          );
        })}
      </div>

      {hasError && !suppressErrorMessage ? (
        <p className="stack-option-field__error">{error}</p>
      ) : null}
    </div>
  );
}
